import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BookOpen, CalendarCheck, ClipboardCheck, CreditCard, FileText, Home, Layers, LayoutDashboard, Library,
  LogOut, Megaphone, Menu, Package, User, Users, Video, BarChart3, ListChecks, Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const STUDENT_NAV = [
  { to: "/siswa", label: "Ringkasan", icon: LayoutDashboard, end: true },
  { to: "/siswa/materi", label: "Materi & Progres", icon: BookOpen },
  { to: "/siswa/kelas", label: "Kelas Live", icon: Video },
  { to: "/siswa/ujian", label: "Ujian & Nilai", icon: ClipboardCheck },
  { to: "/siswa/tugas", label: "Tugas", icon: FileText },
  { to: "/siswa/absensi", label: "Absensi", icon: CalendarCheck },
  { to: "/siswa/profil", label: "Profil & Paket", icon: User },
];

const STAFF_NAV = [
  { to: "/admin", label: "Ringkasan", icon: LayoutDashboard, end: true },
  { to: "/admin/pengguna", label: "Siswa & Akun", icon: Users, adminOnly: true },
  { to: "/admin/paket", label: "Paket Belajar", icon: Package, adminOnly: true },
  { to: "/admin/pembayaran", label: "Pembayaran", icon: CreditCard, adminOnly: true },
  { to: "/admin/kurikulum", label: "Kurikulum", icon: Layers },
  { to: "/admin/materi", label: "Materi", icon: Library },
  { to: "/admin/bank-soal", label: "Bank Soal", icon: ListChecks },
  { to: "/admin/ujian", label: "Ujian", icon: ClipboardCheck },
  { to: "/admin/tugas", label: "Tugas", icon: FileText },
  { to: "/admin/jadwal", label: "Jadwal & Absensi", icon: CalendarCheck },
  { to: "/admin/pengumuman", label: "Pengumuman", icon: Megaphone },
  { to: "/admin/hasil", label: "Hasil & Laporan", icon: BarChart3 },
  { to: "/admin/pengaturan", label: "Tampilan Publik", icon: Settings, adminOnly: true },
];

const ROLE_LABEL = { siswa: "Siswa", pengajar: "Pengajar", admin: "Admin" };

function SideNav({ items, onNavigate }) {
  return (
    <div className="flex h-full flex-col">
      <div className="px-6 h-16 flex items-center border-b border-slate-800"><Logo dark /></div>
      <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1">
        {items.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.end}
            onClick={onNavigate}
            className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-amber-500 text-slate-900" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}
            data-testid={`sidebar-${i.to.split("/").pop() || "root"}-link`}
          >
            <i.icon className="h-4 w-4" />{i.label}
          </NavLink>
        ))}
      </nav>
      <NavLink to="/" className="flex items-center gap-3 px-6 py-4 text-sm text-slate-400 hover:text-white border-t border-slate-800" data-testid="sidebar-home-link"><Home className="h-4 w-4" />Ke Beranda</NavLink>
    </div>
  );
}

export default function DashLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = user.role === "siswa" ? STUDENT_NAV : STAFF_NAV.filter((i) => !i.adminOnly || user.role === "admin");
  const doLogout = async () => {
    await logout();
    nav("/login");
  };
  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-[#0F172A]" data-testid="dashboard-sidebar"><SideNav items={items} /></aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="lg:hidden"><Button variant="ghost" size="icon" data-testid="dashboard-mobile-menu-button"><Menu className="h-5 w-5" /></Button></SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 bg-[#0F172A] border-slate-800">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <SideNav items={items} onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-600" data-testid="dashboard-role-label">Dashboard {ROLE_LABEL[user.role]}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-700 font-medium" data-testid="dashboard-user-name">{user.name}</span>
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-button"><LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Keluar</span></Button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-10 max-w-7xl"><Outlet /></main>
      </div>
    </div>
  );
}
